/**
 * APP INSTALÁVEL + NOTIFICAÇÕES NO CELULAR.
 *
 * O manifesto e o ícone da tela inicial entram AQUI, pelo JavaScript, e não no
 * index.html: o bundler de HTML do Bun tenta resolver todo href absoluto como
 * arquivo do projeto e derruba a página inicial com 500. O servidor serve os
 * arquivos por rota (server/index.ts).
 *
 * As notificações dependem de três coisas que o navegador exige:
 *   • HTTPS (ou localhost);
 *   • o service worker registrado (public/sw.js);
 *   • a PERMISSÃO dada pela pessoa — e só pode ser pedida num clique.
 * No iPhone, além disso, o app precisa estar instalado na tela inicial.
 */
import { api, ApiError } from "./api";

function injetar(rel: string, href: string, extras?: Record<string, string>) {
  if (document.head.querySelector(`link[rel="${rel}"]`)) return;
  const l = document.createElement("link");
  l.rel = rel;
  l.href = href;
  if (extras) for (const [k, v] of Object.entries(extras)) l.setAttribute(k, v);
  document.head.appendChild(l);
}

/**
 * ANUNCIA O APP — chamado no boot (src/main.tsx), ANTES do login.
 *
 * Ficava junto do registro do service worker, que só roda depois de entrar; o
 * efeito era que quem abrisse o endereço e fosse direto em "Adicionar à Tela
 * de Início" instalava um atalho sem nome e sem ícone — no iPhone, que lê o
 * `apple-touch-icon` na hora do toque, o atalho saía com uma miniatura da
 * página de login. O `?v=2` acompanha a troca do ícone: sem ele, quem já tinha
 * instalado continuaria com o desenho antigo, que o iOS guarda para sempre.
 */
export function anunciarApp(): void {
  injetar("manifest", "/manifest.webmanifest");
  injetar("apple-touch-icon", "/apple-touch-icon.png?v=2");
  // O iPhone também usa este para a lista de tamanhos; repetir não atrapalha.
  injetar("apple-touch-icon-precomposed", "/apple-touch-icon.png?v=2");
}

let registro: Promise<ServiceWorkerRegistration | null> | null = null;

/** Registra o service worker (uma vez). Nunca lança. */
export function iniciarPwa(): Promise<ServiceWorkerRegistration | null> {
  if (registro) return registro;
  anunciarApp();
  registro =
    "serviceWorker" in navigator
      ? navigator.serviceWorker.register("/sw.js", { scope: "/" }).catch(() => null)
      : Promise.resolve(null);
  return registro;
}

export type EstadoNotificacao =
  | "ativa" // este aparelho recebe
  | "desligada" // dá para ligar
  | "bloqueada" // a pessoa negou — só nas configurações do navegador
  | "iphone" // iPhone fora do app instalado
  | "indisponivel"; // navegador sem suporte (ou página sem HTTPS)

const ehIphone = () => /iphone|ipad|ipod/i.test(navigator.userAgent);
const instalado = () =>
  window.matchMedia?.("(display-mode: standalone)").matches ||
  (navigator as Navigator & { standalone?: boolean }).standalone === true;

/** Em que pé estão as notificações NESTE aparelho. */
export async function estadoNotificacao(): Promise<EstadoNotificacao> {
  /* O IPHONE VEM PRIMEIRO, de propósito. A partir do iOS 16.4 o Safari já
     mostra `PushManager` na janela mesmo fora do app instalado — mas a
     permissão é recusada na hora de pedir. Testando só o suporte, o cartão
     dizia "Ativar notificações", a pessoa tocava e não acontecia nada. Fora do
     app instalado, a resposta honesta é: instale primeiro. */
  if (ehIphone() && !instalado()) return "iphone";
  if (!("serviceWorker" in navigator) || !("PushManager" in window) || !window.isSecureContext) {
    return "indisponivel";
  }
  if (Notification.permission === "denied") return "bloqueada";
  const reg = await iniciarPwa();
  if (!reg) return "indisponivel";
  const inscricao = await reg.pushManager.getSubscription().catch(() => null);
  return inscricao && Notification.permission === "granted" ? "ativa" : "desligada";
}

const paraBytes = (b64: string) => {
  const pad = "=".repeat((4 - (b64.length % 4)) % 4);
  const bin = atob((b64 + pad).replace(/-/g, "+").replace(/_/g, "/"));
  return Uint8Array.from(bin, (c) => c.charCodeAt(0));
};

/** O que o SERVIDOR sabe deste aparelho (resposta do /push/inscrever). */
export interface SituacaoAparelho {
  ultimo_ok: string | null;
  ultimo_erro: string | null;
  ultimo_erro_em: string | null;
  /** Quantos aparelhos recebem os avisos ao todo. */
  aparelhos: number;
}

/**
 * Garante uma inscrição VÁLIDA neste navegador e registrada no servidor.
 *
 * Três jeitos de o aviso parar de chegar sem ninguém perceber, e o que se faz:
 *   • a inscrição foi feita com outra chave (servidor trocado) → refaz;
 *   • o navegador perdeu a inscrição (dados limpos, expirou) → refaz;
 *   • o serviço de push (Google/Apple) cancelou esta inscrição — o servidor
 *     responde 409 "renovar" → descarta e pede uma nova ao navegador.
 */
async function registrar(reg: ServiceWorkerRegistration): Promise<SituacaoAparelho> {
  const { chave } = (await api.push.chave()) as { chave: string };
  await navigator.serviceWorker.ready;
  let inscricao = await reg.pushManager.getSubscription();
  const atual = inscricao?.options?.applicationServerKey;
  if (inscricao && atual) {
    const a = new Uint8Array(atual);
    const b = paraBytes(chave);
    if (a.length !== b.length || a.some((x, i) => x !== b[i])) {
      await inscricao.unsubscribe().catch(() => {});
      inscricao = null;
    }
  }
  for (let tentativa = 0; tentativa < 2; tentativa++) {
    if (!inscricao) {
      inscricao = await reg.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: paraBytes(chave),
      });
    }
    try {
      return (await api.push.inscrever({
        ...inscricao.toJSON(),
        aparelho: navigator.userAgent.slice(0, 200),
      })) as SituacaoAparelho;
    } catch (e) {
      if (e instanceof ApiError && e.status === 409 && tentativa === 0) {
        await inscricao.unsubscribe().catch(() => {});
        inscricao = null;
        continue;
      }
      throw e;
    }
  }
  throw new Error("Não foi possível registrar este aparelho.");
}

/** Pede a permissão e inscreve este aparelho. Chame dentro de um clique. */
export async function ativarNotificacao(): Promise<EstadoNotificacao> {
  const reg = await iniciarPwa();
  if (!reg) return "indisponivel";
  const permissao = await Notification.requestPermission();
  if (permissao !== "granted") return permissao === "denied" ? "bloqueada" : "desligada";
  await registrar(reg);
  sincronizacao = null; // a próxima conferência pega o estado novo
  return "ativa";
}

let sincronizacao: Promise<SituacaoAparelho | null> | null = null;

/**
 * A CADA ABERTURA do app (src/App.tsx): se a pessoa já deu a permissão, confere
 * a inscrição deste aparelho com o servidor e conserta o que estiver errado —
 * sem pedir nada (a permissão já foi dada). Antes, uma inscrição cancelada pelo
 * Google/Apple deixava o cartão "ligado" e os avisos simplesmente paravam.
 * Devolve o que o servidor sabe do aparelho, ou null se não deu para conferir.
 */
export function sincronizarNotificacao(deNovo = false): Promise<SituacaoAparelho | null> {
  if (sincronizacao && !deNovo) return sincronizacao;
  sincronizacao = (async () => {
    try {
      if (ehIphone() && !instalado()) return null;
      if (!("serviceWorker" in navigator) || !("PushManager" in window) || !window.isSecureContext) return null;
      if (Notification.permission !== "granted") return null;
      const reg = await iniciarPwa();
      if (!reg) return null;
      return await registrar(reg);
    } catch {
      return null;
    }
  })();
  return sincronizacao;
}

/** Computador (sem tela de toque): o aviso depende do navegador estar aberto. */
export const ehComputador = () => !window.matchMedia?.("(pointer: coarse)").matches;

/** Este aparelho para de receber. */
export async function desativarNotificacao(): Promise<void> {
  const reg = await iniciarPwa();
  const inscricao = await reg?.pushManager.getSubscription().catch(() => null);
  if (!inscricao) return;
  await api.push.cancelar(inscricao.endpoint).catch(() => {});
  await inscricao.unsubscribe().catch(() => {});
  sincronizacao = null;
}

/* ---------------- "Instalar app" ----------------
   O Chrome avisa UMA vez, logo no carregamento, que o app pode ser instalado.
   O aviso é guardado aqui para o botão da Agenda usar depois. */
let pedidoInstalar: (Event & { prompt: () => Promise<void> }) | null = null;
if (typeof window !== "undefined") {
  window.addEventListener("beforeinstallprompt", (e) => {
    e.preventDefault();
    pedidoInstalar = e as typeof pedidoInstalar;
    window.dispatchEvent(new Event("sercamp-pode-instalar"));
  });
  window.addEventListener("appinstalled", () => {
    pedidoInstalar = null;
    window.dispatchEvent(new Event("sercamp-pode-instalar"));
  });
}

export const podeInstalar = () => pedidoInstalar !== null;

export async function instalarApp(): Promise<void> {
  const p = pedidoInstalar;
  if (!p) return;
  pedidoInstalar = null;
  await p.prompt().catch(() => {});
  window.dispatchEvent(new Event("sercamp-pode-instalar"));
}
